import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Sparkles,
  Calendar,
  Flame,
  Mail,
  Phone,
  User,
  MapPin,
  Play,
  Pause,
  Volume2,
  VolumeX,
  ArrowRight,
  Quote
} from 'lucide-react';
import WhatsAppIcon from '../components/WhatsAppIcon';
import HomamSubNavbar from '../components/HomamSubNavbar';
import HomamFooter from '../components/HomamFooter';
import HomamFlashTicker from '../components/HomamFlashTicker';
import VideoPosterModal from '../components/VideoPosterModal';
import '../styles/HomamPortal.css';
import '../styles/HomamRegistration.css';
import '../styles/Home.css';
import {
  MOCK_NAKSHATRAS,
  MOCK_SITE_SETTINGS,
  getWhatsAppLink
} from '../data/homamData';
import { getHomamPath } from '../utils/domain';

export default function HomamRegistrationSplitPage() {
  const navigate = useNavigate();
  const [selectedStarId, setSelectedStarId] = useState(1);
  const [bookingError, setBookingError] = useState(null);

  // Form State
  const [bookingForm, setBookingForm] = useState({
    name: '',
    email: '',
    phoneNumber: '',
    whatsappNumber: '',
    location: ''
  });

  // Video Autoplay / Mute / Play state for Hero Split Video
  const heroVideoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true);

  const promoVideoUrl = '/videos/promo_video_01.mp4';
  const promoPosterUrl = '/images/sai_sahasranama_ayushya_homam_banner.jpg';

  const gurujiWhatsApp = getWhatsAppLink({
    customMessage: `Namaste Guruji Seva Team at Sri Gnana Sai Baba Mandir 🙏\nI would like to seek Guruji's blessings and register for the 27 Nakshatra Maha Homam.`,
    source: 'Guruji Guidance Section'
  });

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    document.title = 'Homam Registration (Hero Split Video & Form) | Sri Gnana Sai Baba Mandir';
  }, []);

  // Attempt video play on mount if permitted
  useEffect(() => {
    if (heroVideoRef.current) {
      heroVideoRef.current.muted = isMuted;
      const playPromise = heroVideoRef.current.play();
      if (playPromise !== undefined) {
        playPromise
          .then(() => setIsPlaying(true))
          .catch((err) => {
            console.log('Split video autoplay prevented:', err.message);
            setIsPlaying(false);
          });
      }
    }
  }, [isMuted]);

  const togglePlay = () => {
    if (!heroVideoRef.current) return;
    if (isPlaying) {
      heroVideoRef.current.pause();
      setIsPlaying(false);
    } else {
      heroVideoRef.current
        .play()
        .then(() => setIsPlaying(true))
        .catch((err) => console.log('Play error:', err));
    }
  };

  const toggleMute = () => {
    if (!heroVideoRef.current) return;
    const nextMuted = !isMuted;
    heroVideoRef.current.muted = nextMuted;
    setIsMuted(nextMuted);
  };

  const selectedStar =
    MOCK_NAKSHATRAS.find((star) => star.id === selectedStarId) || MOCK_NAKSHATRAS[0];

  const handleBookingFormChange = (e) => {
    const { name, value } = e.target;
    setBookingForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleBookingSubmit = (e) => {
    e.preventDefault();
    setBookingError(null);

    if (
      !bookingForm.name.trim() ||
      !bookingForm.email.trim() ||
      !bookingForm.phoneNumber.trim() ||
      !bookingForm.whatsappNumber.trim() ||
      !bookingForm.location.trim()
    ) {
      setBookingError('Please fill in all required fields (Name, Email, Phone Number, WhatsApp Number, Location).');
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(bookingForm.email.trim())) {
      setBookingError('Please enter a valid email address.');
      return;
    }

    const cleanPhone = bookingForm.phoneNumber.replace(/\D/g, '');
    if (cleanPhone.length < 10) {
      setBookingError('Please enter a valid 10-digit Phone number.');
      return;
    }

    const cleanWa = bookingForm.whatsappNumber.replace(/\D/g, '');
    if (cleanWa.length < 10) {
      setBookingError('Please enter a valid 10-digit WhatsApp number.');
      return;
    }

    let activeUser = null;
    try {
      const stored = localStorage.getItem('user');
      if (stored) activeUser = JSON.parse(stored);
    } catch (_) {}

    const devoteePayload = {
      name: bookingForm.name.trim(),
      mobile: cleanPhone,
      whatsappNumber: cleanWa,
      email: bookingForm.email.trim(),
      userId: activeUser?._id || null,
      city: bookingForm.location.trim(),
      gothram: '',
      rasi: '',
      message: '',
      nakshatraId: Number(selectedStar.id),
      nakshatraNameEn: selectedStar.nameEn,
      nakshatraNameTa: selectedStar.nameTa,
      homamDate: selectedStar.assignedDate || '',
      preferredDay: `Day ${selectedStar.orderIndex} • ${selectedStar.assignedDate}`,
      participantsCount: 1,
      source: 'Homam Split Hero Page (/homam-registration-split)',
      bookingId: `SGSMH${Date.now().toString().slice(-6)}`,
      _id: 'pending_checkout',
    };

    navigate(getHomamPath('/booking'), {
      state: {
        booking: devoteePayload,
        nakshatra: selectedStar,
      },
    });
  };

  return (
    <div className="homam-reg-page homam-split-page-wrapper">
      {/* 1. Navigation Header */}
      <HomamSubNavbar />

      {/* 2. Schedule Ribbon Flash Ticker */}
      <HomamFlashTicker onSelectStar={(star) => setSelectedStarId(star.id)} />

      {/* ================= 3. HERO SPLIT CAROUSEL SECTION (VIDEO LEFT + FORM RIGHT) ================= */}
      <section className="homam-split-hero-section homam-section-blurred-bg-wrap">
        {/* Blurred Background Image */}
        <div className="homam-section-blurred-bg">
          <img src="/images/saibaba2.jpg" alt="" className="homam-bg-edge-fill" />
          <img src="/images/saibaba2.jpg" alt="Sacred Sai Baba Background" className="homam-bg-complete-img" />
          <div className="homam-section-blurred-overlay" />
        </div>

        <div className="homam-split-hero-container">
          <div className="homam-split-hero-grid">
            
            {/* LEFT COLUMN: HERO VIDEO PLAYER */}
            <div className="homam-split-hero-left">
              <div className="homam-split-video-card">
                <div className="homam-split-video-badge">
                  <Flame className="w-4 h-4 text-amber-400 animate-pulse" />
                  <span>SACRED ANUGRAHA BHASHANAM</span>
                </div>
                
                <div className={`homam-split-video-wrapper ${isPlaying ? 'is-playing' : ''}`}>
                  <video
                    ref={heroVideoRef}
                    src={promoVideoUrl}
                    poster={promoPosterUrl}
                    playsInline
                    muted={isMuted}
                    preload="auto"
                    onPlay={() => setIsPlaying(true)}
                    onPause={() => setIsPlaying(false)}
                    onError={(e) => {
                      if (!e.target.src.includes('/videos/promo_video_01.mp4')) {
                        e.target.src = '/videos/promo_video_01.mp4';
                      }
                    }}
                    className="homam-split-video-element"
                  />
                  
                  {/* Overlay Play/Pause Button */}
                  <button
                    type="button"
                    onClick={togglePlay}
                    className="homam-split-video-overlay-btn"
                    aria-label={isPlaying ? "Pause Video" : "Play Video"}
                  >
                    {isPlaying ? (
                      <Pause className="w-7 h-7 text-amber-300" />
                    ) : (
                      <Play className="w-7 h-7 text-amber-300 fill-amber-300 ml-1" />
                    )}
                  </button>

                  {/* Video Minimal Controls Bar */}
                  <div className="homam-split-video-controls">
                    <button
                      type="button"
                      onClick={togglePlay}
                      className="homam-split-icon-btn"
                      title={isPlaying ? "Pause" : "Play"}
                    >
                      {isPlaying ? <Pause className="w-4 h-4 text-amber-300" /> : <Play className="w-4 h-4 text-amber-300 fill-amber-300" />}
                    </button>
                    <button
                      type="button"
                      onClick={toggleMute}
                      className="homam-split-icon-btn"
                      title={isMuted ? "Unmute" : "Mute"}
                    >
                      {isMuted ? <VolumeX className="w-4 h-4 text-amber-300" /> : <Volume2 className="w-4 h-4 text-emerald-400" />}
                    </button>
                  </div>
                </div>


              </div>
            </div>

            {/* RIGHT COLUMN: BOOKING FORM CONSOLE */}
            <div className="homam-split-hero-right">
              <div className="homam-master-console homam-split-console">
                {/* Ribbon */}
                <div className="homam-console-ribbon">
                  <div className="homam-console-ribbon-text">
                    <span className="homam-console-ribbon-title">
                      27 NAKSHATRA MAHA HOMAM • ONLINE REGISTRATION
                    </span>
                    <span className="homam-console-ribbon-sub">
                      நேரடி ஹோம முன்பதிவு படிவம்
                    </span>
                  </div>
                  <div className="homam-console-ribbon-badge">
                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#10b981', display: 'inline-block', boxShadow: '0 0 8px #10b981' }}></span>
                    <span>Registration Active</span>
                  </div>
                </div>

                {/* Form Body */}
                <div className="homam-split-form-body">
                  <div className="homam-console-card-header" style={{ marginBottom: 14 }}>
                    <div style={{ width: 36, height: 36, borderRadius: 8, background: '#fef3c7', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#b8860b', flexShrink: 0 }}>
                      <Flame className="w-4 h-4" />
                    </div>
                    <div>
                      <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 900, color: '#0f2d5c' }}>
                        Devotee Registration Details
                      </h3>
                      <span style={{ fontSize: '0.8rem', color: '#946608', fontWeight: 700 }}>
                        முன்பதிவு விவரங்களை உள்ளிடுக
                      </span>
                    </div>
                  </div>

                  {bookingError && (
                    <div style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', padding: '9px 13px', borderRadius: 8, fontSize: 12.5, marginBottom: 12 }}>
                      {bookingError}
                    </div>
                  )}

                  <form id="homamSplitForm" onSubmit={handleBookingSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                    {/* Row 1: Name & Email */}
                    <div className="homam-console-form-row">
                      <div className="homam-field">
                        <label style={{ fontSize: 12, fontWeight: 700, color: '#0f2d5c', display: 'flex', alignItems: 'center', gap: 4 }}>
                          <User className="w-3.5 h-3.5" style={{ color: '#b8860b' }} />
                          <span>Devotee Name *</span>
                        </label>
                        <input
                          type="text"
                          name="name"
                          placeholder="Full name"
                          value={bookingForm.name}
                          onChange={handleBookingFormChange}
                          required
                          style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #EADDC7', background: '#FAF7F2', fontSize: 13, color: '#0f2d5c', outline: 'none', boxSizing: 'border-box' }}
                        />
                      </div>

                      <div className="homam-field">
                        <label style={{ fontSize: 12, fontWeight: 700, color: '#0f2d5c', display: 'flex', alignItems: 'center', gap: 4 }}>
                          <Mail className="w-3.5 h-3.5" style={{ color: '#b8860b' }} />
                          <span>Email Address *</span>
                        </label>
                        <input
                          type="email"
                          name="email"
                          placeholder="devotee@gmail.com"
                          value={bookingForm.email}
                          onChange={handleBookingFormChange}
                          required
                          style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #EADDC7', background: '#FAF7F2', fontSize: 13, color: '#0f2d5c', outline: 'none', boxSizing: 'border-box' }}
                        />
                      </div>
                    </div>

                    {/* Row 2: Phone & WhatsApp */}
                    <div className="homam-console-form-row">
                      <div className="homam-field">
                        <label style={{ fontSize: 12, fontWeight: 700, color: '#0f2d5c', display: 'flex', alignItems: 'center', gap: 4 }}>
                          <Phone className="w-3.5 h-3.5" style={{ color: '#b8860b' }} />
                          <span>Phone Number *</span>
                        </label>
                        <input
                          type="tel"
                          name="phoneNumber"
                          maxLength={10}
                          placeholder="10-digit phone number"
                          value={bookingForm.phoneNumber}
                          onChange={handleBookingFormChange}
                          required
                          style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #EADDC7', background: '#FAF7F2', fontSize: 13, color: '#0f2d5c', outline: 'none', boxSizing: 'border-box' }}
                        />
                      </div>

                      <div className="homam-field">
                        <label style={{ fontSize: 12, fontWeight: 700, color: '#0f2d5c', display: 'flex', alignItems: 'center', gap: 4 }}>
                          <WhatsAppIcon size={14} style={{ color: '#25D366' }} />
                          <span>WhatsApp Number *</span>
                        </label>
                        <input
                          type="tel"
                          name="whatsappNumber"
                          maxLength={10}
                          placeholder="10-digit number"
                          value={bookingForm.whatsappNumber}
                          onChange={handleBookingFormChange}
                          required
                          style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #EADDC7', background: '#FAF7F2', fontSize: 13, color: '#0f2d5c', outline: 'none', boxSizing: 'border-box' }}
                        />
                      </div>
                    </div>

                    {/* Row 3: Location */}
                    <div className="homam-field">
                      <label style={{ fontSize: 12, fontWeight: 700, color: '#0f2d5c', display: 'flex', alignItems: 'center', gap: 4 }}>
                        <MapPin className="w-3.5 h-3.5" style={{ color: '#b8860b' }} />
                        <span>City / Location *</span>
                      </label>
                      <input
                        type="text"
                        name="location"
                        placeholder="e.g. Chennai, Bengaluru"
                        value={bookingForm.location}
                        onChange={handleBookingFormChange}
                        required
                        style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #EADDC7', background: '#FAF7F2', fontSize: 13, color: '#0f2d5c', outline: 'none', boxSizing: 'border-box' }}
                      />
                    </div>

                    {/* Row 4: Select Star & Date */}
                    <div className="homam-field">
                      <label style={{ fontSize: 12, fontWeight: 700, color: '#0f2d5c', display: 'flex', alignItems: 'center', gap: 4 }}>
                        <Calendar className="w-3.5 h-3.5" style={{ color: '#b8860b' }} />
                        <span>Select Birth Star & Homam Date *</span>
                      </label>
                      <select
                        name="nakshatraId"
                        value={selectedStarId}
                        onChange={(e) => setSelectedStarId(Number(e.target.value))}
                        required
                        style={{
                          width: '100%',
                          padding: '10px 12px',
                          borderRadius: 8,
                          border: '1.5px solid #d4a017',
                          background: '#FAF7F2',
                          fontSize: 13,
                          fontWeight: 700,
                          color: '#0f2d5c',
                          outline: 'none',
                          cursor: 'pointer'
                        }}
                      >
                        {MOCK_NAKSHATRAS.map((star) => (
                          <option key={star.id} value={star.id}>
                            Day {star.orderIndex}: {star.nameEn} ({star.nameTa}) — {star.assignedDate}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div style={{ marginTop: 6 }}>
                      <button
                        type="submit"
                        className="btn-homam-submit"
                        style={{
                          width: '100%',
                          height: 44,
                          borderRadius: 10,
                          background: 'linear-gradient(135deg, #f5c542 0%, #eab308 40%, #d4a017 100%)',
                          color: '#0a192f',
                          fontWeight: 800,
                          fontSize: 14,
                          border: 'none',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: 8,
                          boxShadow: '0 4px 14px rgba(212, 160, 23, 0.3)'
                        }}
                      >
                        <span>Proceed to Seva Plans</span>
                        <ArrowRight className="w-4 h-4" />
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ================= GURUJI SECTION ================= */}
      <section className="homam-grid-wrap" style={{ background: 'linear-gradient(180deg, #FAF7F2 0%, #F4EFE6 50%, #FAF7F2 100%)', borderTop: '1px solid #EADDC7', padding: '60px 20px' }}>
        <div className="homam-container">
          <div className="homam-guruji-grid">
            {/* Left Image & Video Poster */}
            <div style={{ textAlign: 'center', maxWidth: 440, margin: '0 auto' }}>
              <div style={{ position: 'relative', borderRadius: 28, overflow: 'hidden', border: '3px solid rgba(245, 197, 66, 0.9)', boxShadow: '0 8px 30px rgba(184, 134, 11, 0.2)', background: '#0a192f' }}>
                <img
                  src="/images/guruji.jpg"
                  alt="Guruji - Sri Gnana Sai Baba Mandir"
                  style={{ width: '100%', height: 430, objectFit: 'cover', objectPosition: 'center top', display: 'block' }}
                />
              </div>

              {/* Video Message Player Poster */}
              <VideoPosterModal
                title="Guruji's Sacred Anugraha Bhashanam"
                subtitle="Watch Guruji explain the profound power of 27 Nakshatra Homam"
                posterUrl="/images/sai_sahasranama_ayushya_homam_banner.jpg"
                videoEmbedUrl="/videos/promo_video_01.mp4"
                aspectRatio="16/9"
              />
            </div>

            {/* Right Content */}
            <div style={{ textAlign: 'left' }}>
              <div className="homam-emblem-badge" style={{ marginBottom: 10 }}>
                <Sparkles className="w-3.5 h-3.5" style={{ color: '#b8860b' }} />
                <span>DIVINE GUIDANCE & LEADERSHIP</span>
              </div>

              <h2 style={{ fontSize: '2.2rem', fontWeight: 900, color: '#0f2d5c', margin: '8px 0 16px 0', lineHeight: 1.2 }}>
                Under the Benevolent Grace of <br />
                <span className="gold-text">{MOCK_SITE_SETTINGS.gurujiName}</span>
              </h2>

              {/* Quote Block */}
              <div style={{ background: '#ffffff', borderRadius: 20, border: '2px solid rgba(245, 197, 66, 0.8)', padding: 22, position: 'relative', boxShadow: '0 4px 18px rgba(184, 134, 11, 0.1)', marginBottom: 18 }}>
                <Quote className="w-7 h-7" style={{ color: '#fde68a', position: 'absolute', top: 16, right: 16 }} />
                <p style={{ fontStyle: 'italic', fontSize: 13.5, color: '#1e293b', lineHeight: 1.6, margin: 0 }}>
                  &ldquo;The sacred fire at Sri Gnana Sai Baba Mandir does not just burn wood — it burns away our past karma, worries, and life obstacles. When all 27 Nakshatras are prayed to together, you and your family are blessed with good health, happiness, and divine peace.&rdquo;
                </p>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 10 }}>
                  <Flame className="w-4 h-4" style={{ color: '#b8860b' }} />
                  <span style={{ fontSize: 12, fontWeight: 800, color: '#946608' }}>— {MOCK_SITE_SETTINGS.gurujiName}</span>
                </div>
              </div>

              <p style={{ fontSize: 13, color: '#475569', lineHeight: 1.6, marginBottom: 12 }}>
                {MOCK_SITE_SETTINGS.gurujiBioShort}
              </p>
              <p style={{ fontSize: 13, color: '#475569', lineHeight: 1.6, marginBottom: 20 }}>
                For this sacred 27-day Maha Homam, Guruji has personally blessed the Agni Kundas, selected pure herbal offerings (Samithu), and arranged experienced Vedic scholars to perform continuous prayers and holy rituals for all devotees.
              </p>

              {/* 3 Metrics */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 24 }}>
                <div style={{ background: '#ffffff', padding: '12px 8px', borderRadius: 16, border: '1px solid #EADDC7', textAlign: 'center' }}>
                  <span style={{ fontSize: '1.25rem', fontWeight: 800, color: '#0f2d5c', display: 'block' }}>35+ Yrs</span>
                  <span style={{ fontSize: 11, color: '#64748b' }}>Vedic Tapasya</span>
                </div>
                <div style={{ background: '#ffffff', padding: '12px 8px', borderRadius: 16, border: '1px solid #EADDC7', textAlign: 'center' }}>
                  <span style={{ fontSize: '1.25rem', fontWeight: 800, color: '#0f2d5c', display: 'block' }}>108+</span>
                  <span style={{ fontSize: 11, color: '#64748b' }}>Grand Yagnas</span>
                </div>
                <div style={{ background: '#ffffff', padding: '12px 8px', borderRadius: 16, border: '1px solid #EADDC7', textAlign: 'center' }}>
                  <span style={{ fontSize: '1.25rem', fontWeight: 800, color: '#0f2d5c', display: 'block' }}>50,000+</span>
                  <span style={{ fontSize: 11, color: '#64748b' }}>Devotees Blessed</span>
                </div>
              </div>

              {/* Action CTA: Gold Accent Button */}
              <div>
                <a
                  href={gurujiWhatsApp}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-hero-guruji"
                >
                  <WhatsAppIcon size={18} style={{ color: '#0a192f', marginRight: 8 }} />
                  <span>Connect with Guruji's Seva Team</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <HomamFooter />
    </div>
  );
}
